import { Params } from '@angular/router';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { selectQueryParams, selectRouteParams } from '@planone/shared/util-ngrx';

import { DATA_ACCESS_FAIRS_FEATURE_KEY, FairsState } from './data-access-fairs.reducer';

import { FairsConfiguration } from '../../../interfaces/environment.interfaces';

export const getDataAccessFairsState = createFeatureSelector<FairsState>(DATA_ACCESS_FAIRS_FEATURE_KEY);

export const getFairsConfig = createSelector(getDataAccessFairsState, (state: FairsState) => {
  return state.config;
});

export const getActiveFairId = createSelector(
  getFairsConfig,
  selectRouteParams,
  selectQueryParams,
  (fairsConfig, routerParams: Params, queryParams: Params) => {
    return typeof routerParams !== 'undefined' && routerParams['fair_id'] != null
      ? routerParams['fair_id']
      : typeof routerParams !== 'undefined' && queryParams['fairId'] != null
      ? queryParams['fairId']
      : fairsConfig.bauInsights;
  }
);

export const getActiveTrackingFairId = createSelector(
  selectRouteParams,
  selectQueryParams,
  (routerParams: Params, queryParams: Params) => {
    return typeof routerParams !== 'undefined' && routerParams['fair_id'] != null
      ? routerParams['fair_id']
      : typeof queryParams !== 'undefined' && queryParams['fairId'] != null
      ? queryParams['fairId']
      : 'cross-fair';
  }
);

export const getFairIdFromRoute = createSelector(
  selectRouteParams,
  selectQueryParams,
  (routerParams: Params, queryParams: Params) =>
    !!routerParams && Object.keys(routerParams).includes('fair_id')
      ? routerParams['fair_id']
      : !!queryParams && Object.keys(queryParams).includes('fairId')
      ? queryParams['fairId']
      : undefined
);

export const getFairConfig = createSelector(
  getFairsConfig,
  (fairsConfig: FairsConfiguration, props: { fairId: string }) => ({
    ...fairsConfig.fairs[props.fairId],
    ...(!('advertisements' in (fairsConfig.fairs[props.fairId] ?? {})) && {
      advertisements: {
        parallax: {
          enabled: false,
        },
        skyscraper: {
          enabled: false,
        },
      },
    }),
    id: props.fairId,
  })
);

export const getActiveFairConfig = createSelector(getFairsConfig, getActiveFairId, (fairsConfig, fairId) => ({
  ...fairsConfig.fairs[fairId],
  ...(!('advertisements' in (fairsConfig.fairs[fairId] ?? {})) && {
    advertisements: {
      parallax: {
        enabled: false,
      },
      skyscraper: {
        enabled: false,
      },
    },
  }),
  id: fairId,
}));

export const getActiveFairTimezone = createSelector(getActiveFairConfig, (fairConfig) => {
  return fairConfig.timezone ?? '+0000';
});

export const getFair = createSelector(
  getDataAccessFairsState,
  selectRouteParams,
  getActiveFairId,
  (state: FairsState, routerParams: Params, fairId: string) => {
    return typeof routerParams !== 'undefined' && typeof fairId !== 'undefined'
      ? state.fairs[routerParams[fairId]]
      : undefined;
  }
);

export const getInsightsId = createSelector(getDataAccessFairsState, (state: FairsState) => state.config.bauInsights);

export const getIsBauInsights = createSelector(
  getDataAccessFairsState,
  selectRouteParams,
  selectQueryParams,
  (state: FairsState, routerParams: Params, routerQueryParams: Params) =>
    !routerParams
      ? false
      : state.config.bauInsights === routerParams['fair_id'] ||
        state.config.bauInsights === routerParams['fairId'] ||
        state.config.bauInsights === routerQueryParams['fair_id'] ||
        state.config.bauInsights === routerQueryParams['fairId']
);

export const getFairDirectory = createSelector(getDataAccessFairsState, (state: FairsState) => state.fairs);
export const getFairNextCursor = createSelector(getDataAccessFairsState, (state: FairsState) => state.nextCursor);
export const getActiveFairError = createSelector(getDataAccessFairsState, (state: FairsState) =>
  state.activeFairError ? state.activeFairError : undefined
);
export const getActiveFairLoading = createSelector(
  getDataAccessFairsState,
  (state: FairsState) => state.activeFairLoading
);
